import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Loader2 } from 'lucide-react';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { SocialMediaTabs } from '../components/common/SocialMediaTabs';
import { AccountCard } from '../components/accounts/AccountCard';
import { ConnectAccountModal } from '../components/accounts/ConnectAccountModal';
import { LinkBrandModal } from '../components/accounts/LinkBrandModal';
import { LinkedInOrganizationSelectionModal } from '../components/linkedin/LinkedInOrganizationSelectionModal';
import { useAccounts, type ConnectedPage } from '../hooks/useAccounts.ts';
import { useBrands } from '../hooks/useBrands';
import { useAccountDisconnect, useAccountLinkToBrand, useAccountUnlink } from '../hooks/useAccountMutations';
import { auth } from '../config/firebase';
import type { Brand } from '../types';
import type { SocialPlatform } from '../components/icons/SocialIcon';
import { useAccountsUrlState } from '../store/accountsStore';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';

const Accounts = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const {
    selectedBrand,
    setSelectedBrand,
    activeTab,
    setActiveTab
  } = useAccountsUrlState();
  
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLinkedInOrgsModalOpen, setIsLinkedInOrgsModalOpen] = useState(false);
  const [isLinkBrandModalOpen, setIsLinkBrandModalOpen] = useState(false);
  const [newlyConnectedAccount, setNewlyConnectedAccount] = useState<ConnectedPage | null>(null);
  const [previousAccountIds, setPreviousAccountIds] = useState<string[]>([]);

  // 🧩 1. Détecter needsSelection=true dans l'URL
  const urlParams = new URLSearchParams(location.search);
  const needsSelection = urlParams.get('needsSelection') === 'true';
  const { data, isLoading, error, refetch } = useAccounts();
  const { data: brandsData } = useBrands();
  const accountDisconnectMutation = useAccountDisconnect();
  const accountLinkToBrandMutation = useAccountLinkToBrand();
  const accountUnlinkMutation = useAccountUnlink();
  const connectedPages: ConnectedPage[] = data?.connected_pages || [];
  
  const brands: Brand[] = brandsData ? (brandsData as any).brands?.map((b: any) => ({
    id: b.id, 
    name: b.name, 
    logo: b.logo
  })) || [] : [];
  const allBrandsOption: Brand = { id: 'all', name: 'All Brands', logo: '/logo.svg' };
  
  // Filter brands to only show those with connected accounts
  const brandsWithAccounts = useMemo(() => {
    if (!connectedPages.length) return brands; // If no accounts, show all brands for connection
    
    // Get unique brand IDs from connected pages
    const brandIds = [...new Set(connectedPages.map(page => page.brandId).filter(Boolean))];
    
    // Filter brands to only include those with connected accounts
    return brands.filter((brand: Brand) => brandIds.includes(brand.id));
  }, [brands, connectedPages]);

  const invalidateAccountsCache = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        console.error('No authentication token available');
        return;
      }
      console.log('Invalidating accounts cache via API');
      const response = await fetch(`${API_URL}/management/accounts/invalidate-cache`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        console.error('Failed to invalidate cache:', response.status, response.statusText);
        return;
      }
      const data = await response.json();
      console.log('Cache invalidation response:', data);
    } catch (error) {
      console.error('Error invalidating accounts cache:', error);
    }
  }, []);

  const forceRefreshAccounts = useCallback(async () => {
    console.log('Forcing accounts refetch with cache flush');
    setIsRefreshing(true);
    try {
      await invalidateAccountsCache();
      await queryClient.resetQueries({ queryKey: ['accounts'] });
      await new Promise(resolve => setTimeout(resolve, 100));
      await refetch();
      console.log('Accounts data refetched successfully.');
    } catch (error) {
      console.error('Error refreshing accounts:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [invalidateAccountsCache, queryClient, refetch]);

  const processedCallbackSearch = useRef<string | null>(null);

  useEffect(() => {
    const currentSearch = location.search;
    if (currentSearch === '' || currentSearch === processedCallbackSearch.current) {
        // No search params or already processed this non-empty search string
        if (currentSearch !== '' && currentSearch === processedCallbackSearch.current) {
            console.log('Already processed this callback URL, skipping duplicate action:', currentSearch);
        }
        return;
    }

    processedCallbackSearch.current = currentSearch; // Mark as processed early
    console.log('Processing URL search params:', currentSearch);

    const searchParams = new URLSearchParams(currentSearch);
    const status = searchParams.get('status');
    const platformFromUrl = searchParams.get('platform');
    const accountId = searchParams.get('accountId');

    const oauthCodePresent = searchParams.has('code');
    const oauthTokenPresent = searchParams.has('oauth_token');
    const oauthDenied = searchParams.has('denied');
    const oauthErrorParam = searchParams.get('error');

    const oauthPending = localStorage.getItem('socialness_oauth_pending') === 'true';
    const oauthTimestamp = localStorage.getItem('socialness_oauth_timestamp');
    const isRecentOAuth = oauthTimestamp && (Date.now() - parseInt(oauthTimestamp)) < 5 * 60 * 1000;

    const isBackendConfirmedSuccess = status === 'success' && platformFromUrl && accountId;
    const isLikelyFrontendSuccessOAuth = 
      (oauthCodePresent || oauthTokenPresent) && 
      !oauthDenied && 
      !oauthErrorParam && 
      oauthPending && 
      isRecentOAuth;

    if (isBackendConfirmedSuccess || isLikelyFrontendSuccessOAuth) {
      const successPlatform = platformFromUrl || localStorage.getItem('socialness_oauth_platform');
      console.log(`OAuth success detected for ${successPlatform}. Forcing refresh and cleaning URL.`);

      if (oauthPending) {
        localStorage.removeItem('socialness_oauth_pending');
        localStorage.removeItem('socialness_oauth_platform');
        localStorage.removeItem('socialness_oauth_timestamp');
      }
      
      forceRefreshAccounts().then(() => {
        console.log('Accounts refresh completed after OAuth success.');
        navigate(location.pathname, { replace: true }); // Clean URL
      });
      return; 
    } else if (status === 'error' || oauthDenied || oauthErrorParam) {
      const errorPlatform = platformFromUrl || localStorage.getItem('socialness_oauth_platform');
      console.warn(`OAuth error or denial detected for ${errorPlatform}. Cleaning URL.`);
      if (status === 'error') console.error(`Backend error message: ${searchParams.get('message')}`);
      if (oauthDenied) console.warn(`User denied access for ${errorPlatform}.`);
      if (oauthErrorParam) console.error(`OAuth error parameter for ${errorPlatform}: ${oauthErrorParam}`);

      if (oauthPending) {
        localStorage.removeItem('socialness_oauth_pending');
        localStorage.removeItem('socialness_oauth_platform');
        localStorage.removeItem('socialness_oauth_timestamp');
      }
      navigate(location.pathname, { replace: true }); // Clean URL
    }
    // If no specific OAuth callback params were handled, clear the processed flag
    // to allow re-processing if the search string somehow remains but wasn't an OAuth callback.
    // This might not be strictly necessary if navigate always cleans the URL effectively.
    // else {
    //   processedCallbackSearch.current = null;
    // }

  }, [location.search, location.pathname, forceRefreshAccounts, navigate]);

  const filteredPages = selectedBrand === 'all' 
    ? (activeTab === 'all' ? connectedPages : connectedPages.filter((page: ConnectedPage) => page.platform === activeTab))
    : (activeTab === 'all' 
        ? connectedPages.filter((page: ConnectedPage) => page.brandId === selectedBrand)
        : connectedPages.filter((page: ConnectedPage) => page.brandId === selectedBrand && page.platform === activeTab));

  const handleConnect = async (platform: SocialPlatform) => {
    console.log('Platform selected for connection:', platform);
    try {
      await invalidateAccountsCache();
      setIsConnectModalOpen(false);
    } catch (error) {
      console.error('Error preparing for OAuth connection:', error);
    }
  };

  const handleDisconnect = async (pageId: string) => {
    try {
      await accountDisconnectMutation.mutateAsync(pageId);
      await forceRefreshAccounts();
    } catch (error) {
      console.error('Error disconnecting account:', error);
      alert(t('accounts.errors.disconnectFailed', 'Failed to disconnect account. Please try again.'));
    }
  };

  const handleLinkToBrand = async (pageId: string, brandId: string) => {
    try {
      const page = connectedPages.find((p: ConnectedPage) => p.id === pageId);
      if (!page) {
        console.error('Page not found for linking:', pageId);
        alert(t('accounts.errors.linkFailed', 'Failed to link account. Page not found.'));
        return;
      }
      await accountLinkToBrandMutation.mutateAsync({
        accountId: page.parentAccountId,
        pageId,
        brandId
      });
      await forceRefreshAccounts();
    } catch (error) {
      console.error('Error linking account to brand:', error);
      alert(t('accounts.errors.linkFailed', 'Failed to link account to brand. Please try again.'));
    }
  };

  const handleUnlinkFromBrand = async (pageId: string) => {
    try {
      const page = connectedPages.find((p: ConnectedPage) => p.id === pageId);
      if (!page) {
        console.error('Page not found for unlinking:', pageId);
        alert(t('accounts.errors.unlinkFailed', 'Failed to unlink account. Page not found.'));
        return;
      }
      await accountUnlinkMutation.mutateAsync({
        accountId: page.parentAccountId,
        pageId
      });
      await forceRefreshAccounts();
    } catch (error) {
      console.error('Error unlinking account from brand:', error);
      alert(t('accounts.errors.unlinkFailed', 'Failed to unlink account from brand. Please try again.'));
    }
  };

  const getBrandInfo = (brandId: string) => {
    return brands.find((brand: Brand) => brand.id === brandId);
  };

  // Function to detect newly connected accounts and handle auto-linking
  const handleNewlyConnectedAccounts = useCallback((currentPages: ConnectedPage[]) => {
    console.log('Checking for newly connected accounts...');
    
    if (previousAccountIds.length === 0) {
      // First load, just store current IDs
      setPreviousAccountIds(currentPages.map(p => p.id));
      return;
    }
    
    // Find newly connected accounts (not linked to any brand)
    const newAccounts = currentPages.filter(page => 
      !previousAccountIds.includes(page.id) && !page.brandId
    );
    
    console.log('New unlinked accounts found:', newAccounts.length > 0 ? newAccounts.map(p => ({ name: p.name, platform: p.platform })) : 'None');
    
    if (newAccounts.length > 0) {
      const newAccount = newAccounts[0]; // Handle the first new account
      console.log('Processing new account:', newAccount.name);
      
      // Filter out brands that don't have a valid ID
      const availableBrands = brands.filter(brand => brand.id && brand.id !== 'all');
      
             if (availableBrands.length === 1) {
         // Auto-link to the single brand
         console.log('Auto-linking to single brand:', availableBrands[0].name);
         // We'll handle the auto-linking directly here
         const autoLinkToBrand = async () => {
           try {
             await accountLinkToBrandMutation.mutateAsync({
               accountId: newAccount.parentAccountId,
               pageId: newAccount.id,
               brandId: availableBrands[0].id
             });
             await forceRefreshAccounts();
           } catch (error) {
             console.error('Error auto-linking account to brand:', error);
           }
         };
         autoLinkToBrand();
       } else if (availableBrands.length > 1) {
        // Open modal for brand selection
        console.log('Opening LinkBrandModal for brand selection');
        setNewlyConnectedAccount(newAccount);
        setIsLinkBrandModalOpen(true);
      }
    }
    
    // Update previous account IDs
    setPreviousAccountIds(currentPages.map(p => p.id));
  }, [previousAccountIds, brands, accountLinkToBrandMutation, forceRefreshAccounts]);

  // Effect to detect newly connected accounts
  useEffect(() => {
    if (connectedPages.length > 0 && !isLoading) {
      handleNewlyConnectedAccounts(connectedPages);
    }
  }, [connectedPages, isLoading, handleNewlyConnectedAccounts]);

  // 🧱 2. Ouvrir la modal des organisations LinkedIn si nécessaire
  React.useEffect(() => {
    if (needsSelection) {
      setIsLinkedInOrgsModalOpen(true);
    }
  }, [needsSelection]);

  return (
    <>
      <PageLayout
        title={t('accounts.title')}
        subtitle={t('accounts.subtitle')}
        actions={
          <div className="flex items-center gap-3">
            <button 
              onClick={forceRefreshAccounts} 
              className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={isRefreshing}
            >
              {isRefreshing ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
            </button>
            <button 
              onClick={() => setIsConnectModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('accounts.connect')}
            </button>
          </div>
        }
      >

          <div className="mb-4 flex justify-end">
            <BrandDropdown
              brands={[allBrandsOption, ...brandsWithAccounts]}
              selectedBrandId={selectedBrand}
              onBrandSelect={setSelectedBrand}
              className="w-40"
            />
          </div>

          <div className="mb-8">
            <SocialMediaTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              showSummary={false}
              showAllTab={true}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {isLoading && (
              <>
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="bg-white rounded-xl border border-black shadow-lg p-6 animate-pulse">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                          <div className="absolute -bottom-1 -right-1 bg-white rounded-lg p-0.5 border border-black">
                            <div className="w-3.5 h-3.5 bg-gray-200 rounded"></div>
                          </div>
                        </div>
                        <div>
                          <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
                          <div className="h-4 w-24 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                      <div className="w-5 h-5 bg-gray-200 rounded"></div>
                    </div>
                    <div className="pt-4 border-t border-gray-100">
                      <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
                      <div className="h-8 w-full bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </>
            )}
            
            {error && (
              <div className="col-span-full text-center py-12">
                <p className="text-red-500">{t('accounts.errors.loadFailed', 'Failed to load accounts. Please try again later.')}</p>
              </div>
            )}
            
            {!isLoading && !error && filteredPages.length === 0 && (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">{t('accounts.noAccounts', 'No accounts found for the selected filter.')}</p>
                <button 
                  onClick={() => setIsConnectModalOpen(true)}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {t('accounts.connectFirst')}
                </button>
              </div>
            )}

            {filteredPages.map((page: ConnectedPage) => {
              const brand = getBrandInfo(page.brandId);
              return (
                <AccountCard
                  key={page.id}
                  id={page.id}
                  name={page.name}
                  username={page.username}
                  platform={page.platform}
                  pictureUrl={page.picture_url}
                  followersCount={page.followers_count}
                  isVerified={page.is_verified}
                  brandId={page.brandId}
                  brandName={brand?.name}
                  brandLogo={brand?.logo}
                  onDisconnect={handleDisconnect}
                  onLinkToBrand={handleLinkToBrand}
                  onUnlinkFromBrand={handleUnlinkFromBrand}
                  availableBrands={brands}
                />
              );
            })}
          </div>
      </PageLayout>

      <ConnectAccountModal
        isOpen={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
        onConnect={handleConnect}
        existingPlatforms={connectedPages.map((p: ConnectedPage) => p.platform)}
      />

      <LinkedInOrganizationSelectionModal
        isOpen={isLinkedInOrgsModalOpen}
        onClose={() => {
          setIsLinkedInOrgsModalOpen(false);
          // Nettoyer l'URL après fermeture
          navigate('/social-media-hub', { replace: true });
        }}
        onComplete={() => {
          setIsLinkedInOrgsModalOpen(false);
          // Nettoyer l'URL et rafraîchir les comptes
          navigate('/social-media-hub', { replace: true });
          forceRefreshAccounts();
        }}
        onError={(error) => {
          console.error('LinkedIn pages connection error:', error);
          // Vous pouvez afficher une notification d'erreur ici si nécessaire
        }}
      />

      {newlyConnectedAccount && (
        <LinkBrandModal
          isOpen={isLinkBrandModalOpen}
          onClose={() => {
            setIsLinkBrandModalOpen(false);
            setNewlyConnectedAccount(null);
          }}
          onSelect={async (brandId: string) => {
            try {
              console.log('Linking account to selected brand:', brandId);
              await accountLinkToBrandMutation.mutateAsync({
                accountId: newlyConnectedAccount.parentAccountId,
                pageId: newlyConnectedAccount.id,
                brandId
              });
              await forceRefreshAccounts();
              setIsLinkBrandModalOpen(false);
              setNewlyConnectedAccount(null);
            } catch (error) {
              console.error('Error linking account to selected brand:', error);
              alert(t('accounts.errors.linkFailed', 'Failed to link account to brand. Please try again.'));
            }
          }}
          brands={brands.filter(brand => brand.id && brand.id !== 'all')}
          accountName={newlyConnectedAccount.name}
          platform={newlyConnectedAccount.platform as SocialPlatform}
          isLoading={accountLinkToBrandMutation.isPending}
        />
      )}
    </>
  );
};

export default Accounts;